import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Whether the page in view holds a change that is not saved, and the question "leave
 * anyway?" while it is asked. UI state only (FE-STATE-02). It lives in shared because the
 * page that has the change and the header's engine selector are different features, and a
 * feature may not reach into another (FE-ARCH-02). The page that marks it renders the
 * confirmation and answers it.
 */
export const useUnsavedChangesStore = defineStore('unsaved-changes', () => {
  const hasUnsavedChanges = ref(false)
  /** True while the operator is being asked whether to leave. */
  const confirming = ref(false)
  let pending: ((leave: boolean) => void) | null = null

  function mark(value: boolean) {
    hasUnsavedChanges.value = value
  }

  /** Resolves true when nothing is unsaved, or when the operator chooses to leave. */
  function confirmLeave(): Promise<boolean> {
    if (!hasUnsavedChanges.value) return Promise.resolve(true)
    // Only one question at a time: a newer one replaces an unanswered one, which stays.
    pending?.(false)
    confirming.value = true
    return new Promise((resolve) => (pending = resolve))
  }

  function answer(leave: boolean) {
    const resolve = pending
    pending = null
    confirming.value = false
    if (leave) hasUnsavedChanges.value = false
    resolve?.(leave)
  }

  /** The page is gone, so nothing is left to lose. */
  function clear() {
    if (pending) answer(true)
    hasUnsavedChanges.value = false
  }

  return { hasUnsavedChanges, confirming, mark, confirmLeave, answer, clear }
})
